import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTaskStatus } from '../APIManager/BulkAddCourseSectionManger'

const TERMINAL_STATUSES = new Set(['SUCCESSFUL', 'WARNING', 'ERROR', 'COMPLETED', 'FAILED', 'REVOKED'])
const BASE_INTERVAL_MS = 4000
const MAX_INTERVAL_MS = 60000

interface UseReportRegenerationTaskArgs {
  onSuccess: () => void
  onError: () => void
}

interface UseReportRegenerationTaskResult {
  start: (taskId: string) => void
  isPolling: boolean
}

/**
 * Polls a single report-regeneration Celery task by id until it reaches a terminal
 * status, then fires onSuccess/onError exactly once. Exponential backoff (4s → 60s).
 * Account Owner is a global role, so task-status polling is permitted.
 */
export const useReportRegenerationTask = ({ onSuccess, onError }: UseReportRegenerationTaskArgs): UseReportRegenerationTaskResult => {
  const [taskId, setTaskId] = useState<string | null>(null)
  const pollCountRef = useRef(0)
  const handledRef = useRef(false)
  // Keep callbacks in a ref so the terminal effect always sees the latest closures.
  const callbacksRef = useRef({ onSuccess, onError })
  callbacksRef.current = { onSuccess, onError }

  const start = (id: string): void => {
    handledRef.current = false
    pollCountRef.current = 0
    setTaskId(id)
  }

  const { data: taskData } = useQuery({
    queryKey: ['reportRegenerationTask', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('No taskId')
      return await getTaskStatus(taskId)
    },
    enabled: !!taskId,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status as string | undefined
      if (status != null && TERMINAL_STATUSES.has(status)) return false
      return Math.min(BASE_INTERVAL_MS * Math.pow(2, pollCountRef.current), MAX_INTERVAL_MS)
    }
  })

  useEffect(() => {
    const status = taskData?.data?.status as string | undefined
    if (!status) return
    if (!TERMINAL_STATUSES.has(status)) {
      pollCountRef.current += 1
      return
    }
    if (handledRef.current) return
    handledRef.current = true

    const result = taskData?.data?.result as string | undefined
    const isSuccess = status === 'SUCCESSFUL' || status === 'WARNING' ||
      (status === 'COMPLETED' && result !== 'ERROR')

    if (isSuccess) callbacksRef.current.onSuccess()
    else callbacksRef.current.onError()

    setTaskId(null)
  }, [taskData])

  return { start, isPolling: !!taskId }
}
